import { t } from './labels';
import type { FilmstripAdapter, RuntimeView, ViewCompletionState } from './types';

const PREVIEW_ATTR = 'data-fotomania-filmstrip-preview';
const ACTIVE_ATTR = 'data-fotomania-filmstrip-preview-active';
const STYLE_ATTR = 'data-fotomania-filmstrip-preview-style';
const INCOMPLETE_ATTR = 'data-fotomania-filmstrip-incomplete';
const PENDING_ATTR = 'data-fotomania-filmstrip-pending';
const ITEM_SELECTOR = '.fm-view-thumbnails__item';
const THUMB_SELECTOR = '.fm-view-thumbnails__thumb';

function findFilmstrip(root: ParentNode): HTMLElement | null {
  const direct = root.querySelector<HTMLElement>('fm-view-thumbnails');
  if (direct) return direct;

  for (const element of root.querySelectorAll<HTMLElement>('*')) {
    if (element.shadowRoot) {
      const nested = findFilmstrip(element.shadowRoot);
      if (nested) return nested;
    }
  }
  return null;
}

export function createFilmstripAdapter(
  customizer: Element,
  onUserSelection: (index: number) => void = () => {},
): FilmstripAdapter {
  let previews = new Map<number, string>();
  let views: readonly RuntimeView[] = [];
  let completion = new Map<number, ViewCompletionState>();
  let disconnected = false;
  let renderScheduled = false;
  let filmstripRoot: ShadowRoot | null = null;
  const observedRoots = new WeakSet<Node>();

  const Observer = globalThis.MutationObserver;
  const observer =
    typeof Observer === 'function' ? new Observer(() => scheduleRender()) : null;

  const observe = (node: Node | null): void => {
    if (!node || observedRoots.has(node)) return;
    observedRoots.add(node);
    observer?.observe(node, { childList: true, subtree: true });
  };

  const removePresentation = (root: ParentNode): void => {
    root.querySelectorAll(`[${PREVIEW_ATTR}="true"]`).forEach((node) => node.remove());
    root.querySelectorAll<HTMLElement>(`[${ACTIVE_ATTR}]`).forEach((node) => node.removeAttribute(ACTIVE_ATTR));
    root.querySelectorAll(`[${STYLE_ATTR}]`).forEach((node) => node.remove());
    root.querySelectorAll<HTMLElement>(`[${INCOMPLETE_ATTR}]`).forEach((node) => node.removeAttribute(INCOMPLETE_ATTR));
    root.querySelectorAll(`[${PENDING_ATTR}]`).forEach((node) => node.remove());
  };

  const selectionIndex = (event: Event): number | null => {
    if (!(event.target instanceof Element) || !filmstripRoot) return null;
    if (event.target.closest('.fm-view-thumbnails__lock-btn, .fm-view-thumbnails__unlock-btn, .fm-view-thumbnails__unit-actions')) return null;
    const item = event.target.closest<HTMLElement>(ITEM_SELECTOR);
    if (!item) return null;
    const items = [...filmstripRoot.querySelectorAll<HTMLElement>(ITEM_SELECTOR)];
    const index = items.indexOf(item);
    return index >= 0 ? index : null;
  };

  const claimSelection = (event: Event): void => {
    const index = selectionIndex(event);
    if (index === null) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    onUserSelection(index);
  };

  const onClick = (event: Event): void => claimSelection(event);

  const onKeyDown = (event: Event): void => {
    const keyboard = event as KeyboardEvent;
    if (keyboard.key !== 'Enter' && keyboard.key !== ' ') return;
    claimSelection(event);
  };

  const wireRoot = (nextRoot: ShadowRoot): void => {
    if (filmstripRoot === nextRoot) return;
    if (filmstripRoot) {
      filmstripRoot.removeEventListener('click', onClick, true);
      filmstripRoot.removeEventListener('keydown', onKeyDown, true);
      removePresentation(filmstripRoot);
    }
    filmstripRoot = nextRoot;
    filmstripRoot.addEventListener('click', onClick, true);
    filmstripRoot.addEventListener('keydown', onKeyDown, true);
    observe(filmstripRoot);
  };

  const ensureStyle = (root: ShadowRoot): void => {
    if (root.querySelector(`[${STYLE_ATTR}]`)) return;
    const style = document.createElement('style');
    style.setAttribute(STYLE_ATTR, '');
    style.textContent = `
      ${THUMB_SELECTOR}[${ACTIVE_ATTR}] > .fm-view-thumbnails__placeholder,
      ${THUMB_SELECTOR}[${ACTIVE_ATTR}] > img:not([${PREVIEW_ATTR}="true"]) { display: none !important; }
      ${THUMB_SELECTOR} > img[${PREVIEW_ATTR}="true"] { display: block; width: 100%; height: 100%; object-fit: contain; }
      ${ITEM_SELECTOR}[${INCOMPLETE_ATTR}="true"] { position: relative; }
      ${ITEM_SELECTOR}[${INCOMPLETE_ATTR}="true"] ${THUMB_SELECTOR} { outline: 2px solid oklch(0.55 0.20 25); outline-offset: 2px; }
      [${PENDING_ATTR}] { position: absolute; top: .25rem; right: .25rem; min-width: 1.25rem; height: 1.25rem; padding: 0 .25rem; border-radius: 999px; background: oklch(0.55 0.20 25); color: white; font: 700 .75rem/1.25rem sans-serif; text-align: center; }
    `;
    root.appendChild(style);
  };

  const render = (): void => {
    renderScheduled = false;
    if (disconnected) return;

    observe(customizer);
    observe(customizer.shadowRoot);
    const filmstrip = findFilmstrip(customizer.shadowRoot ?? customizer);
    if (!filmstrip?.shadowRoot) return;
    wireRoot(filmstrip.shadowRoot);
    ensureStyle(filmstripRoot!);

    const items = [...filmstripRoot!.querySelectorAll<HTMLElement>(ITEM_SELECTOR)];
    items.forEach((item, index) => {
      const thumb = item.querySelector<HTMLElement>(THUMB_SELECTOR);
      if (!thumb) return;
      const url = previews.get(index);
      let image = thumb.querySelector<HTMLImageElement>(`img[${PREVIEW_ATTR}="true"]`);

      const state = completion.get(index);
      let pending = item.querySelector<HTMLElement>(`[${PENDING_ATTR}]`);
      if (state && !state.complete && state.pendingSlotCount > 0) {
        item.setAttribute(INCOMPLETE_ATTR, 'true');
        if (!pending) {
          pending = document.createElement('span');
          pending.setAttribute(PENDING_ATTR, '');
          pending.setAttribute('aria-label', t('filmstrip_module.pending_photos'));
          item.appendChild(pending);
        }
        const count = String(state.pendingSlotCount);
        if (pending.textContent !== count) pending.textContent = count;
      } else {
        item.removeAttribute(INCOMPLETE_ATTR);
        pending?.remove();
      }

      if (!url) {
        image?.remove();
        thumb.removeAttribute(ACTIVE_ATTR);
        return;
      }

      if (!image) {
        image = document.createElement('img');
        image.setAttribute(PREVIEW_ATTR, 'true');
        thumb.appendChild(image);
      }
      image.src = url;
      image.alt = views[index]?.title ?? item.getAttribute('aria-label') ?? t('filmstrip_module.view_number', { number: index + 1 });
      thumb.setAttribute(ACTIVE_ATTR, 'true');
    });
  };

  function scheduleRender(): void {
    if (disconnected || renderScheduled) return;
    renderScheduled = true;
    queueMicrotask(render);
  }

  observe(customizer);
  observe(customizer.shadowRoot);
  render();

  return {
    apply(nextPreviews: ReadonlyMap<number, string>, nextViews: readonly RuntimeView[]): void {
      if (disconnected) return;
      previews = new Map(nextPreviews);
      views = [...nextViews];
      render();
    },
    applyCompletion(nextCompletion: readonly ViewCompletionState[]): void {
      if (disconnected) return;
      completion = new Map(nextCompletion.map((view) => [view.viewIndex, view]));
      render();
    },
    disconnect(): void {
      if (disconnected) return;
      disconnected = true;
      observer?.disconnect();
      if (filmstripRoot) {
        filmstripRoot.removeEventListener('click', onClick, true);
        filmstripRoot.removeEventListener('keydown', onKeyDown, true);
        removePresentation(filmstripRoot);
      }
      previews.clear();
      views = [];
      completion.clear();
      filmstripRoot = null;
    },
  };
}
